import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { formatPrice, formatImageUrl, orderStatusNames, paymentMethodNames, shippingMethodNames } from '../utils/helpers';
import { CheckCircle, Package, Truck, CreditCard, Loader2, XCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderSuccess = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  // Stripe payment polling state: null | 'checking' | 'paid' | 'pending' | 'expired' | 'error'
  const [payStatus, setPayStatus] = useState(sessionId ? 'checking' : null);

  useEffect(() => {
    fetchOrder();
  }, [id]);

  // Poll Stripe payment status when returning from checkout
  useEffect(() => {
    if (!sessionId) return;

    let attempts = 0;
    const maxAttempts = 6;
    let timer;

    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/payments/checkout/status/${sessionId}`);
        const { payment_status, status } = res.data;
        if (payment_status === 'paid') {
          setPayStatus('paid');
          fetchOrder();
          return;
        }
        if (status === 'expired') {
          setPayStatus('expired');
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          setPayStatus('pending');
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (err) {
        console.error('Payment status check failed:', err);
        setPayStatus('error');
      }
    };

    poll();
    return () => timer && clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/orders/${id}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="relative min-h-screen pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-300">Betöltés...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="relative min-h-screen pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-300">Rendelés nem található</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Header */}
        <div className="text-center mb-8">
          <CheckCircle className="w-20 h-20 mx-auto mb-4 text-green-400" />
          <h1 className="section-title text-4xl mb-2">
            <span>Köszönjük a rendelésed!</span>
          </h1>
          <p className="text-gray-400">Rendelésszám: <span className="text-yellow-200 font-semibold">{order.order_number}</span></p>
        </div>

        {/* Stripe payment status banner */}
        {payStatus === 'checking' && (
          <div className="glass rounded-xl p-4 mb-6 flex items-center gap-3 bg-blue-500/10 border-blue-500/30">
            <Loader2 className="w-5 h-5 text-blue-300 animate-spin" />
            <span className="text-blue-200">Fizetés ellenőrzése folyamatban...</span>
          </div>
        )}
        {payStatus === 'paid' && (
          <div className="glass rounded-xl p-4 mb-6 flex items-center gap-3 bg-green-500/10 border-green-500/30">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-green-300">Sikeres fizetés! Köszönjük a vásárlást. 🌙</span>
          </div>
        )}
        {(payStatus === 'pending') && (
          <div className="glass rounded-xl p-4 mb-6 flex items-center gap-3 bg-yellow-500/10 border-yellow-500/30">
            <Loader2 className="w-5 h-5 text-yellow-300" />
            <span className="text-yellow-200">A fizetés feldolgozás alatt áll. A visszaigazolást e-mailben küldjük.</span>
          </div>
        )}
        {(payStatus === 'expired' || payStatus === 'error') && (
          <div className="glass rounded-xl p-4 mb-6 flex items-center gap-3 bg-red-500/10 border-red-500/30">
            <XCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300">A fizetés nem fejeződött be. Kérlek próbáld újra a kosárból.</span>
          </div>
        )}

        {/* Order Details */}
        <div className="space-y-6">
          {/* Payment Info */}
          {order.payment_method === 'transfer' && order.payment_status === 'pending' && (
            <div className="glass rounded-xl p-6 bg-yellow-500/5 border-yellow-500/20">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-yellow-200" />
                Fizetési információk
              </h2>
              <p className="text-gray-300 mb-4">
                Kérjük, utalj át az alábbi számlára:
              </p>
              <div className="bg-black/30 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Számlaszám:</span>
                  <span className="font-mono">12345678-12345678-12345678</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Közlemény:</span>
                  <span className="font-mono">{order.order_number}</span>
                </div>
                <div className="flex justify-between text-xl font-bold">
                  <span className="text-gray-400">Összeg:</span>
                  <span className="text-yellow-200">{formatPrice(order.total)}</span>
                </div>
              </div>
              <p className="text-sm text-gray-400 mt-4">
                💬 A rendelést az átutalás beérkezése után indítjuk útnak.
              </p>
            </div>
          )}

          {/* Order Items */}
          <div className="glass rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-yellow-200" />
              Megrendelt termékek
            </h2>
            <div className="space-y-3">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    {item.image && (
                      <img src={formatImageUrl(item.image)} alt={item.name} className="w-12 h-12 object-cover rounded" />
                    )}
                    <div>
                      <p className="font-semibold">{item.name}</p>
                      <p className="text-sm text-gray-400">{item.quantity} db × {formatPrice(item.price)}</p>
                    </div>
                  </div>
                  <p className="font-semibold text-yellow-200">{formatPrice(item.price * item.quantity)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Shipping & Billing */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {order.shipping_address && (
            <div className="glass rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Truck className="w-5 h-5 text-yellow-200" />
                Szállítási cím
              </h2>
              <div className="text-sm text-gray-300 space-y-1">
                <p>{order.shipping_address.name}</p>
                <p>{order.shipping_address.phone}</p>
                <p>{order.shipping_address.address}</p>
                <p>{order.shipping_address.zip} {order.shipping_address.city}</p>
                <p>{order.shipping_address.country}</p>
              </div>
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-sm text-gray-400">Szállítási mód</p>
                <p className="text-white">{shippingMethodNames[order.shipping_method]}</p>
              </div>
            </div>
            )}

            {order.billing_address && (
            <div className="glass rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-3">Számlázási cím</h2>
              <div className="text-sm text-gray-300 space-y-1">
                <p>{order.billing_address.name}</p>
                <p>{order.billing_address.address}</p>
                <p>{order.billing_address.zip} {order.billing_address.city}</p>
                <p>{order.billing_address.country}</p>
                {order.billing_address.tax_number && (
                  <p className="pt-2">Adószám: {order.billing_address.tax_number}</p>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="glass rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-4">Összegzés</h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-400">Részösszeg</span>
                <span>{formatPrice(order.subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Szállítás</span>
                <span>{formatPrice(order.shipping_cost)}</span>
              </div>
              <div className="border-t border-white/10 pt-2 mt-2">
                <div className="flex justify-between text-xl font-bold">
                  <span>Végösszeg</span>
                  <span className="text-yellow-200">{formatPrice(order.total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate('/termekek')}
              className="flex-1 btn-magic rounded-lg py-3 font-semibold"
            >
              További vásárlás
            </button>
            <button
              onClick={() => navigate('/profil')}
              className="flex-1 glass rounded-lg py-3 hover:bg-white/5 transition"
            >
              Rendeléseim
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
        .section-title {
          font-family: "Great Vibes", cursive;
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }
        .section-title span {
          color: #ffffff;
        }
        .btn-magic {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          border: 1px solid hsl(0 0% 100% / 0.14);
        }
        .btn-magic:before {
          content: '';
          position: absolute;
          inset: -200%;
          background: conic-gradient(from 0deg, transparent, hsl(45 86% 62% / 0.25), transparent 30%);
          animation: spin 6s linear infinite;
          z-index: -1;
          filter: blur(12px);
        }
        @keyframes spin {
          to { transform: rotate(1turn); }
        }
      `}</style>
    </div>
  );
};

export default OrderSuccess;